import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import MetricCard from '@/components/admin/MetricCard';
import KPIGrid from '@/components/admin/KPIGrid';
import { Users, BarChart3, DollarSign, UserPlus, ExternalLink, UserCheck, Share2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';

export default function AdminBusinessDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-business-kpis'],
    queryFn: async () => {
      // Users
      const { count: totalUsers } = await supabase.from('users').select('*', { count: 'exact', head: true });
      const { count: activeUsers30d } = await supabase
        .from('chat_sessions')
        .select('*', { count: 'exact', head: true })
        .gte('updated_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      // Posits
      const { count: totalPosits } = await supabase.from('posits').select('*', { count: 'exact', head: true });

      // Subscriptions (basic count placeholder)
      const { count: activeSubs } = await supabase
        .from('posit_subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      // Affiliates (clicks/referrals simplified)
      const { count: referrals30d } = await supabase
        .from('referrals')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      return {
        totalUsers: totalUsers ?? 0,
        activeUsers30d: activeUsers30d ?? 0,
        totalPosits: totalPosits ?? 0,
        activeSubs: activeSubs ?? 0,
        referrals30d: referrals30d ?? 0,
      };
    },
    enabled: !!user,
  });

  return (
    <DashboardLayout title="Business Dashboard" description="High‑level business KPIs with drill‑downs" maxWidth="7xl">
      <KPIGrid>
        <MetricCard title="Total Users" value={isLoading ? '…' : metrics?.totalUsers} icon={<Users className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/business/users')} />
        <MetricCard title="Active Users (30d)" value={isLoading ? '…' : metrics?.activeUsers30d} icon={<UserCheck className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/business/users')} />
        <MetricCard title="Total Posits" value={isLoading ? '…' : metrics?.totalPosits} icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/business/posits')} />
        <MetricCard title="Active Subscriptions" value={isLoading ? '…' : metrics?.activeSubs} icon={<UserPlus className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/business/subscriptions')} />
        <MetricCard title="Affiliate Referrals (30d)" value={isLoading ? '…' : metrics?.referrals30d} icon={<Share2 className="h-4 w-4 text-muted-foreground" />} onClick={() => navigate('/admin/business/affiliates')} />
      </KPIGrid>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-1 md:col-span-2 p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">Users</h2>
            <button className="text-sm text-primary" onClick={() => navigate('/admin/business/users')}>View all</button>
          </div>
          <p className="text-sm text-muted-foreground">Click to see a table of users with filters and detail view.</p>
        </div>
        <div className="p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">Marketing</h2>
            <button className="text-sm text-primary" onClick={() => navigate('/admin/business/marketing')}>Open</button>
          </div>
          <p className="text-sm text-muted-foreground">Top‑of‑funnel metrics powered by PostHog via MCP.</p>
        </div>
      </div>
    </DashboardLayout>
  );
}

